import React from "react";
import { UiSwitch } from "@/components/ui";
import { useNotifications } from "@/hooks/useNotifications";
import { Bell, BellOff, Settings } from "lucide-react";

interface NotificationSettingsProps {
  className?: string;
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  className,
}) => {
  const {
    subscriptions,
    toggleSubscription,
    requestNotificationPermission,
    isConnected,
  } = useNotifications();

  const topicLabels = {
    "/campagne": "Campagnes",
    "/commande": "Commandes",
    "/utilisateur": "Utilisateurs",
  };

  const topicDescriptions = {
    "/campagne":
      "Notifications sur les nouvelles campagnes et leurs mises à jour",
    "/commande": "Notifications sur les commandes et leur statut",
    "/utilisateur": "Notifications sur les utilisateurs et les actions système",
  };

  React.useEffect(() => {
    // Demander la permission pour les notifications browser au montage
    requestNotificationPermission();
  }, []);

  return (
    <div className={`notification-settings ${className || ""}`}>
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-800">
          Paramètres de notifications
        </h3>
        <div className="flex items-center gap-2 ml-auto">
          {isConnected ? (
            <>
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-green-600">Connecté</span>
            </>
          ) : (
            <>
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-sm text-red-600">Déconnecté</span>
            </>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <p className="text-sm text-gray-600 mb-4">
          Choisissez les types de notifications que vous souhaitez recevoir en
          temps réel.
        </p>

        {subscriptions.map((subscription) => (
          <div
            key={subscription.topic}
            className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start gap-3 flex-1">
              <div className="mt-1">
                {subscription.enabled ? (
                  <Bell className="w-5 h-5 text-blue-600" />
                ) : (
                  <BellOff className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-800">
                  {topicLabels[subscription.topic as keyof typeof topicLabels]}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {
                    topicDescriptions[
                      subscription.topic as keyof typeof topicDescriptions
                    ]
                  }
                </p>
              </div>
            </div>

            <div className="ml-4">
              <UiSwitch
                defaultValue={subscription.enabled}
                onChange={() => toggleSubscription(subscription.topic)}
                label=""
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-800">
              Notifications du navigateur
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              Pour recevoir des notifications même lorsque l'application n'est
              pas active, autorisez les notifications dans votre navigateur.
            </p>
            {Notification.permission === "default" && (
              <button
                onClick={requestNotificationPermission}
                className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                Autoriser les notifications
              </button>
            )}
            {Notification.permission === "granted" && (
              <span className="text-sm text-green-700 mt-2 block">
                ✓ Notifications autorisées
              </span>
            )}
            {Notification.permission === "denied" && (
              <span className="text-sm text-red-700 mt-2 block">
                ✗ Notifications refusées. Veuillez les autoriser dans les
                paramètres de votre navigateur.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
